import { useState, useEffect } from 'react';
import { theme } from '../theme';
import VerLinksButton from './VerLinksButton';

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString('es-CO')}`;

// ── Gráfica de barras simple (SVG puro, sin dependencias) ──
function BarChart({ data, labels, color }) {
  const max = Math.max(1, ...data);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '160px', marginTop: '0.5rem' }}>
      {data.map((value, i) => {
        const h = Math.round((value / max) * 100);
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
            <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
              <div
                title={`${labels[i]}: ${value}`}
                style={{
                  width: '70%',
                  minHeight: value > 0 ? '4px' : '0',
                  height: `${h}%`,
                  background: `linear-gradient(180deg, ${color}, ${color}99)`,
                  borderRadius: '5px 5px 0 0',
                  position: 'relative',
                  transition: 'height 0.4s ease',
                }}
              >
                {value > 0 && (
                  <span style={{
                    position: 'absolute', top: '-18px', left: '50%', transform: 'translateX(-50%)',
                    fontSize: '0.72rem', fontWeight: 700, color: theme.colors.text, whiteSpace: 'nowrap',
                  }}>
                    {value}
                  </span>
                )}
              </div>
            </div>
            <span style={{ fontSize: '0.7rem', color: theme.colors.textMuted, marginTop: '6px' }}>{labels[i]}</span>
          </div>
        );
      })}
    </div>
  );
}

function ChartCard({ icon, title, total, subtitle, data, labels, color }) {
  return (
    <div style={{
      backgroundColor: theme.colors.surface,
      borderRadius: '14px',
      border: `1px solid ${theme.colors.border}`,
      padding: '1.5rem',
      boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.2rem' }}>
        <h3 style={{ color: theme.colors.text, fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>
          {icon} {title}
        </h3>
        <span style={{ color, fontSize: '1.5rem', fontWeight: 800, lineHeight: 1 }}>{total}</span>
      </div>
      {subtitle && (
        <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', margin: '0 0 0.3rem' }}>{subtitle}</p>
      )}
      <BarChart data={data} labels={labels} color={color} />
    </div>
  );
}

function Dashboard({ token, username }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch('/api/dashboard/weekly', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { if (alive) { setData(d); setError(false); } })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const sum = (arr) => (arr || []).reduce((a, b) => a + b, 0);

  return (
    <main style={{ padding: '2.5rem 1.5rem', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.8rem' }}>
        <h1 style={{ color: theme.colors.primary, fontSize: '1.6rem', margin: '0 0 0.3rem' }}>
          📊 Resumen de la semana
        </h1>
        <p style={{ color: theme.colors.textMuted, fontSize: '0.92rem', margin: 0 }}>
          ¡Hola{username ? `, ${username}` : ''}! 👋 Este es el desempeño de tu negocio
          {data?.week ? ` (${data.week.start} → ${data.week.end})` : ' esta semana'}.
        </p>
      </div>

      <div style={{ marginBottom: '1.8rem' }}>
        <VerLinksButton />
      </div>

      {loading ? (
        <p style={{ color: theme.colors.textMuted }}>Cargando tus métricas...</p>
      ) : error ? (
        <p style={{ color: theme.colors.error }}>No se pudieron cargar las métricas. Intenta de nuevo más tarde.</p>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(330px, 100%), 1fr))',
          gap: '1.5rem',
        }}>
          <ChartCard
            icon="🛒"
            title="Pedidos realizados"
            total={sum(data.pedidos_realizados.counts)}
            subtitle={`Total facturado: ${fmtMoney(sum(data.pedidos_realizados.amounts))}`}
            data={data.pedidos_realizados.counts}
            labels={data.labels}
            color="#38bdf8"
          />
          <ChartCard
            icon="📅"
            title="Citas por atender"
            total={sum(data.citas_pedidas.counts)}
            subtitle="Citas a atender esta semana"
            data={data.citas_pedidas.counts}
            labels={data.labels}
            color="#a78bfa"
          />
          <ChartCard
            icon="💳"
            title="Pedidos pagados"
            total={sum(data.pedidos_pagados.counts)}
            subtitle={`Ingresos por pedidos: ${fmtMoney(sum(data.pedidos_pagados.amounts))}`}
            data={data.pedidos_pagados.counts}
            labels={data.labels}
            color="#4ade80"
          />
          <ChartCard
            icon="💰"
            title="Citas pagadas"
            total={sum(data.citas_pagadas.counts)}
            subtitle={`Ingresos por citas: ${fmtMoney(sum(data.citas_pagadas.amounts))}`}
            data={data.citas_pagadas.counts}
            labels={data.labels}
            color="#fbbf24"
          />
        </div>
      )}
    </main>
  );
}

export default Dashboard;
